import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import studio from "../../styles/studioPage.module.css";
import adminSurface from "../../components/layouts/AdminSurface.module.css";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import Dropdown from "../../components/common/Dropdown";
import Button from "../../components/ui/Button";
import {
    getWalkingTourTracks,
    getTrackWalkingMode,
    switchTrackWalkingMode,
    TrackWalkingModeResponse,
    WalkingTourMode,
    WalkingTourTrackItem,
} from "../../api/administration";
import { useNotification } from "../../context/NotificationContext";
import styles from "./WalkingTourSwitch.module.css";

const WalkingTourSwitch: React.FC = () => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { showNotification } = useNotification();

    const [tracks, setTracks] = useState<WalkingTourTrackItem[]>([]);
    const [bothFiltersExist, setBothFiltersExist] = useState(true);
    const [tracksLoading, setTracksLoading] = useState(true);
    const [selectedTrackId, setSelectedTrackId] = useState<string>("");
    const [walkingInfo, setWalkingInfo] = useState<TrackWalkingModeResponse | null>(null);
    const [infoLoading, setInfoLoading] = useState(false);
    const [selectedMode, setSelectedMode] = useState<WalkingTourMode | null>(null);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchTracks = async () => {
            setTracksLoading(true);
            try {
                const token = localStorage.getItem("token");
                if (!token) return;
                const response = await getWalkingTourTracks({ token });
                setBothFiltersExist(response.bothFiltersExist);
                setTracks(response.tracks || []);
            } catch (error) {
                console.error("Failed to fetch walking tour tracks", error);
                showNotification({
                    message: t("walkingSwitchLoadError"),
                    type: "error",
                });
            } finally {
                setTracksLoading(false);
            }
        };
        fetchTracks();
    }, [showNotification, t]);

    const trackOptions = useMemo(
        () =>
            tracks.map((track) => {
                const modeLabel =
                    track.currentMode === "free"
                        ? t("freeWalk")
                        : track.currentMode === "guided"
                          ? t("guidedWalk")
                          : "";
                const modeSuffix = modeLabel ? ` — ${modeLabel}` : "";
                return {
                    value: track.id,
                    label: `${track.name || t("untitled")}${modeSuffix} (${track.id})`,
                };
            }),
        [tracks, t],
    );

    const loadWalkingMode = useCallback(
        async (trackId: string) => {
            if (!trackId) {
                setWalkingInfo(null);
                setSelectedMode(null);
                return;
            }

            setInfoLoading(true);
            try {
                const token = localStorage.getItem("token");
                if (!token) return;
                const response = await getTrackWalkingMode({ token, trackId });
                setWalkingInfo(response);
                setSelectedMode(response.currentMode);
            } catch (error: any) {
                console.error("Failed to fetch walking mode", error);
                setWalkingInfo(null);
                setSelectedMode(null);
                showNotification({
                    message: error?.message || t("walkingSwitchLoadError"),
                    type: "error",
                });
            } finally {
                setInfoLoading(false);
            }
        },
        [showNotification, t],
    );

    const handleTrackChange = (trackId: string) => {
        setSelectedTrackId(trackId);
        loadWalkingMode(trackId);
    };

    const refreshTrackList = useCallback(async () => {
        try {
            const token = localStorage.getItem("token");
            if (!token) return;
            const response = await getWalkingTourTracks({ token });
            setBothFiltersExist(response.bothFiltersExist);
            setTracks(response.tracks || []);
        } catch (error) {
            console.error("Failed to refresh walking tour tracks", error);
        }
    }, []);

    const handleConfirm = async () => {
        if (!selectedTrackId || !selectedMode || !walkingInfo?.isEligible) return;
        if (selectedMode === walkingInfo.currentMode) return;

        const confirmed = window.confirm(
            t("walkingSwitchConfirm", {
                mode: selectedMode === "free" ? t("freeWalk") : t("guidedWalk"),
            }),
        );
        if (!confirmed) return;

        setSaving(true);
        try {
            const token = localStorage.getItem("token");
            if (!token) return;

            await switchTrackWalkingMode({
                token,
                trackId: selectedTrackId,
                mode: selectedMode,
            });

            showNotification({
                message: t("walkingSwitchSuccess"),
                type: "success",
            });
            await Promise.all([loadWalkingMode(selectedTrackId), refreshTrackList()]);
        } catch (error: any) {
            console.error("Failed to switch walking mode", error);
            const messageKey = error?.message;
            const translated =
                messageKey === "filters-not-available"
                    ? t("walkingSwitchFiltersMissing")
                    : messageKey === "track-not-walking-tour"
                      ? t("walkingSwitchNotEligible")
                      : t("walkingSwitchError");
            showNotification({
                message: translated,
                type: "error",
            });
        } finally {
            setSaving(false);
        }
    };

    const switchDisabled = !walkingInfo?.isEligible || infoLoading || saving || !bothFiltersExist;
    const canSave =
        !!walkingInfo?.isEligible &&
        !!selectedMode &&
        selectedMode !== walkingInfo.currentMode &&
        !saving &&
        !infoLoading &&
        bothFiltersExist;

    const currentModeLabel =
        walkingInfo?.currentMode === "free"
            ? t("freeWalk")
            : walkingInfo?.currentMode === "guided"
              ? t("guidedWalk")
              : t("walkingSwitchUnknownMode");

    const modeOptions: { mode: WalkingTourMode; label: string; subtitle: string }[] = [
        { mode: "guided", label: t("guidedWalk"), subtitle: t("guidedWalkSub") },
        { mode: "free", label: t("freeWalk"), subtitle: t("freeWalkSub") },
    ];

    return (
        <div className={adminSurface.pageContent}>
            <div className={`${studio.pageTitle} ${adminSurface.pageTitle}`}>
                <TitleSubtitle title={t("walkingTourSwitch")} subtitle={t("walkingTourSwitchSubtitle")} />
            </div>

            {!tracksLoading && !bothFiltersExist && (
                <p className={adminSurface.warnBanner}>{t("walkingSwitchFiltersMissing")}</p>
            )}

            <div className={styles.panel}>
                <div className={styles.trackField}>
                    <Dropdown
                        label={t("selectTrack")}
                        value={selectedTrackId}
                        options={trackOptions}
                        onChange={handleTrackChange}
                        placeholder={
                            !bothFiltersExist
                                ? t("walkingSwitchFiltersMissing")
                                : tracks.length === 0
                                  ? t("walkingSwitchNoTracks")
                                  : t("selectATrack")
                        }
                        loading={tracksLoading}
                        disabled={tracksLoading || saving || !bothFiltersExist}
                        error={false}
                    />
                </div>

                {selectedTrackId && bothFiltersExist && (
                    <>
                        {infoLoading ? (
                            <p className={styles.loading}>{t("loading")}</p>
                        ) : walkingInfo ? (
                            <>
                                <div className={styles.cur}>
                                    {t("walkingSwitchCurrentMode")}: <b>{currentModeLabel}</b>
                                </div>

                                {!walkingInfo.isEligible && (
                                    <p className={styles.ineligible}>{t("walkingSwitchNotEligible")}</p>
                                )}

                                <div className={styles.modeField}>
                                    <div className={styles.radios}>
                                        {modeOptions.map(({ mode, label, subtitle }) => (
                                            <button
                                                key={mode}
                                                type="button"
                                                className={`${styles.radio}${selectedMode === mode ? ` ${styles.radioSelected}` : ""}${switchDisabled ? ` ${styles.radioDisabled}` : ""}`}
                                                onClick={() => !switchDisabled && setSelectedMode(mode)}
                                                disabled={switchDisabled}
                                            >
                                                <i className={styles.radioDot} aria-hidden />
                                                <span className={styles.radioText}>
                                                    <b>{label}</b>
                                                    <span>{subtitle}</span>
                                                </span>
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div>
                                    <Button
                                        label={saving ? t("saving") : t("walkingSwitchApply")}
                                        variant="primary"
                                        shape="default"
                                        className={styles.applyBtn}
                                        onClick={handleConfirm}
                                        disabled={!canSave}
                                    />
                                </div>
                            </>
                        ) : null}
                    </>
                )}
            </div>
        </div>
    );
};

export default WalkingTourSwitch;
